import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { env } from '../lib/env';
import { getSupabaseClient } from '../lib/supabase';
import { getSupabaseRuntimeIdentity } from '../features/runtime/supabaseAdapter';
import type { RuntimeActorRole, RuntimeIdentityV1 } from '../features/runtime/types';

type NavItem = { to: string; label: string };

type Props = {
  nav: NavItem[];
  roleLabel: string;
  allowedRoles?: RuntimeActorRole[];
};

export function RoleShell({ nav, roleLabel, allowedRoles = [] }: Props) {
  const client = useMemo(() => getSupabaseClient(), []);
  const navigate = useNavigate();
  const location = useLocation();
  const [identity, setIdentity] = useState<RuntimeIdentityV1 | null>(null);
  const [checking, setChecking] = useState(Boolean(env.supabaseUrl && env.supabasePublishableKey));
  const [accessError, setAccessError] = useState('');

  useEffect(() => {
    if (!env.supabaseUrl || !env.supabasePublishableKey || !client) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const session = await client.auth.getSession();
      if (cancelled) return;
      if (!session.data.session) {
        navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
        return;
      }
      try {
        const current = await getSupabaseRuntimeIdentity();
        if (cancelled) return;
        setIdentity(current);
        if (allowedRoles.length > 0 && !current?.roles.some((item) => allowedRoles.includes(item.role))) {
          setAccessError(`Akun ini tidak mempunyai akses ${roleLabel}.`);
        }
      } catch (error) {
        if (!cancelled) setAccessError(error instanceof Error ? error.message : 'Identity tidak dapat diverifikasi.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [allowedRoles, client, location.pathname, location.search, navigate, roleLabel]);

  async function logout() {
    if (client) await client.auth.signOut();
    navigate('/login', { replace: true });
  }

  if (checking) return <main className="app-main"><section className="card">Memverifikasi akun…</section></main>;
  if (accessError) return <main className="app-main"><section className="feedback feedback--error"><strong>Akses ditolak.</strong><p>{accessError}</p></section></main>;

  return (
    <div className="app-shell">
      <header className="app-header no-print">
        <div className="app-header__inner">
          <BrandLogo />
          <div>
            <div className="muted" style={{ marginBottom: 6 }}>
              {roleLabel}{identity?.displayName ? ` · ${identity.displayName}` : ''}
            </div>
            <nav className="nav" aria-label={`Navigasi ${roleLabel}`}>
              {nav.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to.split('/').filter(Boolean).length === 1}>
                  {item.label}
                </NavLink>
              ))}
              {client && env.supabaseUrl && env.supabasePublishableKey ? (
                <button className="nav-logout" type="button" onClick={() => void logout()}>Keluar</button>
              ) : null}
            </nav>
          </div>
        </div>
      </header>
      <main className="app-main"><Outlet /></main>
    </div>
  );
}
